// utils/chequeValidators.js

// Validation rules as per documentation
export const chequeValidationRules = {
  bankId: {
    required: true,
    message: 'Bank account is required'
  },
  beneficiaryName: {
    required: true,
    maxLength: 200,
    message: 'Beneficiary name is required and must be less than 200 characters'
  },
  chqAmount: {
    required: true,
    min: 0.01,
    type: 'number',
    message: 'Amount must be greater than 0'
  },
  chqDate: {
    required: true,
    type: 'date',
    message: 'Cheque date is required'
  },
  remarks: {
    maxLength: 500,
    message: 'Remarks must be less than 500 characters'
  }
};

// Validation function
export const validateChequeForm = (formData) => {
  const errors = {};

  // Validate each field
  Object.keys(chequeValidationRules).forEach(field => {
    const rule = chequeValidationRules[field];
    const value = formData[field];

    if (rule.required && (!value || value.toString().trim() === '')) {
      errors[field] = rule.message;
      return;
    }

    if (value && rule.maxLength && value.length > rule.maxLength) {
      errors[field] = rule.message;
      return;
    }

    if (value && rule.min && parseFloat(value) <= rule.min) {
      errors[field] = rule.message;
      return;
    }

    if (value && rule.type === 'date' && !isValidDate(value)) {
      errors[field] = rule.message;
      return;
    }
  });

  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

// Date validation helper
const isValidDate = (dateString) => {
  const date = new Date(dateString);
  return date instanceof Date && !isNaN(date);
};

// Input sanitization functions
export const sanitizeInput = (input) => {
  if (typeof input !== 'string') return input;
  
  return input
    .trim()
    .replace(/[<>]/g, '') // Remove potential HTML tags
    .substring(0, 1000); // Limit length
};

export const sanitizeChequeData = (data) => ({
  ...data,
  beneficiaryName: sanitizeInput(data.beneficiaryName),
  remarks: sanitizeInput(data.remarks),
  chqAmount: parseFloat(data.chqAmount) || 0
});

// Amount to words conversion (simplified)
export const convertAmountToWords = (amount) => {
  if (!amount || isNaN(amount)) return '';
  
  const num = parseFloat(amount);
  if (num === 0) return 'Zero Dollars Only';
  
  // This is a simplified implementation
  // In production, you should use a proper number-to-words library
  const dollars = Math.floor(num);
  const cents = Math.round((num - dollars) * 100);
  
  let result = '';
  
  if (dollars > 0) {
    result += numberToWords(dollars) + ' Dollar' + (dollars > 1 ? 's' : '');
  }
  
  if (cents > 0) {
    if (result) result += ' and ';
    result += numberToWords(cents) + ' Cent' + (cents > 1 ? 's' : '');
  }
  
  return result + ' Only';
};

// Helper function for number to words (simplified)
const numberToWords = (num) => {
  const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
  const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
  const thousands = ['', 'Thousand', 'Million', 'Billion'];

  if (num === 0) return 'Zero';
  if (num < 10) return ones[num];
  if (num < 20) return teens[num - 10];
  if (num < 100) return tens[Math.floor(num / 10)] + (num % 10 !== 0 ? ' ' + ones[num % 10] : '');
  if (num < 1000) return ones[Math.floor(num / 100)] + ' Hundred' + (num % 100 !== 0 ? ' ' + numberToWords(num % 100) : '');
  
  // Handle thousands, millions, etc.
  let result = '';
  let thousandIndex = 0;
  
  while (num > 0) {
    if (num % 1000 !== 0) {
      result = numberToWords(num % 1000) + 
               (thousands[thousandIndex] ? ' ' + thousands[thousandIndex] : '') + 
               (result ? ' ' + result : '');
    }
    num = Math.floor(num / 1000);
    thousandIndex++;
  }
  
  return result;
};

// Cheque number validation
export const validateChequeNumber = (chequeNumber) => {
  // Field is required
  if (!chequeNumber || chequeNumber.toString().trim() === '') {
    return { isValid: false, message: 'Cheque number is required' };
  }

  const cleanNumber = chequeNumber.toString().trim();

  // Must be numeric only
  if (!/^\d+$/.test(cleanNumber)) {
    return { isValid: false, message: 'Cheque number must contain only digits' };
  }

  if (cleanNumber.length > 6) return { isValid: false, message: 'Cheque number must be 6 digits or less' };

  return { isValid: true, message: '' };
};

// Format cheque number to minimum 6 digits with leading zeros
export const formatChequeNumber = (chequeNumber) => {
  if (!chequeNumber || chequeNumber.toString().trim() === '') return '';
  const cleanNumber = chequeNumber.toString().trim();
  // Only pad with zeros if the value is purely numeric
  if (/^\d+$/.test(cleanNumber) && cleanNumber.length < 6) {
    return cleanNumber.padStart(6, '0');
  }
  return cleanNumber;
};

// Date validation for cheques
export const validateChequeDate = (date) => {
  if (!date) return { isValid: false, message: 'Date is required' };
  
  const chequeDate = new Date(date);
  const today = new Date();
  const sixMonthsFromNow = new Date();
  sixMonthsFromNow.setMonth(today.getMonth() + 6);
  
  if (isNaN(chequeDate.getTime())) {
    return { isValid: false, message: 'Invalid date format' };
  }
  
  if (chequeDate < today.setHours(0, 0, 0, 0)) {
    return { isValid: false, message: 'Cheque date cannot be in the past' };
  }
  
  if (chequeDate > sixMonthsFromNow) {
    return { isValid: false, message: 'Cheque date cannot be more than 6 months in the future' };
  }
  
  return { isValid: true, message: '' };
};

// Export default validation function
export default validateChequeForm; 